/**
 * Every NON-CHARGE network write the Admin Invoice Workbench makes.
 *
 * These are thin wrappers over the SAME service functions the production
 * invoice uses. Each sends the same URL, the same body type and the same key
 * set. `manage-invoice` takes exactly one key per request: that is five
 * separate call sites in production (AdminActions.jsx:89, 156, 231, 306, 430),
 * never a combined payload, and the endpoint has never received one.
 *
 * Charge writes live in ./chargeModel.js, which calls addCharge and
 * updateInvoice directly. Together the two files are the complete write
 * surface; see COVERAGE.md. (An earlier draft reached the charge endpoints by
 * mounting the production <BillingAdmin/> instead — this comment described
 * that design and outlived it.)
 */

import { updateAdminShipment } from "../../../services/admin.services";
import { confirmOneTimeShipperShipment } from "../../../services/admin.services";
import {
  uploadInternationalDocument,
  deleteInternationalDocument,
} from "../../../services/shipments.services";

/** The ten values the backend accepts, byte-for-byte. Order matches the
 *  production Select. "none" is the disabled placeholder, never sent. */
export const INVOICE_STATUS_OPTIONS = [
  "Unpaid",
  "Extra Charge Paid",
  "Less Charge Paid",
  "Paid",
  "Disputed",
  "Duty Disputed",
  "Dispute Won",
  "Dispute Lost",
  "Refunded",
  "Other",
];

/** Production's truthiness rule for the toggles. Mirrors AdminActions.jsx:476
 *  exactly — note the string forms, which a plain Boolean() would get wrong. */
export const isChecked = (value) =>
  !(value === 0 || value === "0" || value === "false" || value === false);

const oneKey = (key, value) => {
  const formData = new FormData();
  formData.append(key, value);
  return formData;
};

/* ─── manage-invoice: one key per request ──────────────────────────────── */

export const saveAdminInvoiceId = (shipmentId, adminInvoiceId) =>
  updateAdminShipment(oneKey("admin_invoice_id", adminInvoiceId), shipmentId);

export const saveInvoiceDescription = (shipmentId, description) =>
  updateAdminShipment(oneKey("invoice_description", description), shipmentId);

/** Sends the status string verbatim. Callers must pass a value from
 *  INVOICE_STATUS_OPTIONS — an out-of-enum string is accepted by the endpoint
 *  and then renders in the admin grid as an unfilterable row. */
export const saveAdminInvoiceStatus = (shipmentId, status) =>
  updateAdminShipment(oneKey("admin_invoice_status", status), shipmentId);

/** `field` is "is_bill_verified" or "is_duty_paid". Value is 1 or 0, matching
 *  updateShipmentToggle in production (AdminActions.jsx:418, 428). */
export const saveShipmentToggle = (shipmentId, field, checked) =>
  updateAdminShipment(oneKey(field, checked ? 1 : 0), shipmentId);

/** attachment[0..n], alone. Takes an array — callers Array.from() the
 *  FileList first. (Production calls .forEach on the FileList directly. That
 *  does NOT throw: html2pdf.js, imported by InvoiceModal.jsx:18, bundles
 *  core-js's web.dom-collections.for-each, which puts forEach on
 *  FileList.prototype. Same files, same order, same keys; see COVERAGE.md.) */
export const saveAttachments = (shipmentId, files) => {
  const formData = new FormData();
  (files || []).forEach((file, index) =>
    formData.append(`attachment[${index}]`, file),
  );
  return updateAdminShipment(formData, shipmentId);
};

/* ─── one-time shipper ─────────────────────────────────────────────────── */

/** POST with NO body — axios is called with no second argument in production
 *  and the endpoint expects that. */
export const confirmOneTimeShipper = (shipmentId) =>
  confirmOneTimeShipperShipment(shipmentId);

/* ─── international documents ──────────────────────────────────────────── */

/** Note the service takes (shipmentId, data) — the opposite order to the
 *  admin.services functions above. Easy to get backwards. */
export const uploadCustomsDocument = (shipmentId, document, documentType) => {
  const formData = new FormData();
  formData.append("document", document);
  formData.append("document_type", documentType);
  return uploadInternationalDocument(shipmentId, formData);
};

/** The shipment id comes off the DOCUMENT ROW (doc.shipping_id), not from the
 *  shipment being viewed. Production does the same; they can differ. */
export const deleteCustomsDocument = (doc) =>
  deleteInternationalDocument(doc?.shipping_id, doc?.id);

/* ─── document type map ────────────────────────────────────────────────── */

/** The key is what goes on the wire as `document_type`; the value is the
 *  label. Copied from InvoiceActionsWithDocs.jsx:13-27. */
export const DOCUMENT_TYPE_MAP = {
  "001": "Authorization_Form",
  "002": "Commercial_Invoice",
  "003": "Certificate_of_Origin",
  "004": "Export_Accompanying_Document",
  "005": "Export_License",
  "006": "Import_Permit",
  "007": "One_Time_USMCA",
  "008": "Other_Document",
  "009": "Power_of_Attorney",
  "010": "Packing_List",
  "011": "SED_Document",
  "012": "Shippers_Letter_of_Instruction",
  "013": "Declaration",
};
